import { InvestorDetailsCommitmentsTabTableDataQuery, InvestorDetailsHouseholdCommitmentsTabTableDataQuery } from '@/__generated__/graphql';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { InvestorDetailsEntityTypeEnum } from '@/components/investors-page/investorDetailsPage/types';
import { MobileRow, MobileRowTitle, MobileRowValue, MobileTableRowWrap } from '@/components/table/mobileTable/mobileTable';
import { TableBodyWrapper, TableRowCell, TextCell } from '@/components/table/styles';
import { TableColumns } from '@/components/table/types';
import { formatCash } from '@/shared/utils/currency';
import { useMemo } from 'react';
import styled from 'styled-components';

type RowSumType = {
  total: string;
  committedCapital: number;
  capitalCalled: number;
  capitalRemaining: number;
  distributionsToDate: number;
  marketValue: number;
};

interface CommitmentsMobileTableProps {
  tableColumns: TableColumns[];
  tableRows: InvestorDetailsCommitmentsTabTableDataQuery['ClientEntityDetailsCommitments']['commitments'];
  entityType: InvestorDetailsEntityTypeEnum;
  householdCommitmentsTabTableTotals: InvestorDetailsHouseholdCommitmentsTabTableDataQuery['investorHouseholdCommitments']['totals'] | null;
}

const CommitmentsMobileSumFooter = ({ tableColumns, tableRows, entityType, householdCommitmentsTabTableTotals }: CommitmentsMobileTableProps) => {
  const rowSum: RowSumType = useMemo(() => {
    if (entityType === InvestorDetailsEntityTypeEnum.Household) {
      const { committedCapital, capitalCalled, capitalRemaining, distributionsToDate, marketValue } = householdCommitmentsTabTableTotals || {};
      return {
        total: 'Total',
        committedCapital: committedCapital || 0,
        capitalCalled: capitalCalled || 0,
        capitalRemaining: capitalRemaining || 0,
        distributionsToDate: distributionsToDate || 0,
        marketValue: marketValue || 0
      };
    }
    const sum = {
      total: 'Total',
      committedCapital: 0,
      capitalCalled: 0,
      capitalRemaining: 0,
      distributionsToDate: 0,
      marketValue: 0
    };
    for (let i = 0; i < tableRows.length; i++) {
      sum.committedCapital += tableRows[i].committedCapital;
      sum.capitalCalled += tableRows[i].capitalCalled;
      sum.capitalRemaining += tableRows[i].capitalRemaining;
      sum.distributionsToDate += +tableRows[i].distributionsToDate;
      sum.marketValue += +tableRows[i].marketValue;
    }
    return sum;
  }, [tableRows, entityType, householdCommitmentsTabTableTotals]);

  const toolTipContent = (row: RowSumType) => {
    return (
      <TableBodyWrapper>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.committedCapital)} Committed Capital</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.capitalCalled)} Capital Called</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.capitalRemaining)} Capital Remaining</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          {entityType === InvestorDetailsEntityTypeEnum.Household ? (
            <TextCell>{householdCommitmentsTabTableTotals?.capitalCalledPercentage?.toFixed(2)}% Called to Date</TextCell>
          ) : (
            <TextCell>{((row.capitalCalled / row.committedCapital) * 100).toFixed(2)}% Called to Date</TextCell>
          )}
        </TableRowCell>
      </TableBodyWrapper>
    );
  };

  return (
    <>
      {tableRows && (
        <MobileSumFooterWrap>
          <MobileRow>
            <MobileRowTitle>{rowSum.total}</MobileRowTitle>
          </MobileRow>

          <MobileRow>
            <MobileRowTitle>{tableColumns[2].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip>{rowSum.committedCapital}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>

          <MobileRow>
            <MobileRowTitle>{tableColumns[3].title}</MobileRowTitle>
            <MobileRowValue>
              <Tooltip title={toolTipContent(rowSum)}>
                {entityType === InvestorDetailsEntityTypeEnum.Household ? (
                  <>{householdCommitmentsTabTableTotals?.capitalCalledPercentage?.toFixed(2)}%</>
                ) : (
                  <>
                    {rowSum.capitalCalled === 0 || rowSum.committedCapital === 0
                      ? '0%'
                      : ((rowSum.capitalCalled / rowSum.committedCapital) * 100).toFixed(2) + '%'}
                  </>
                )}
              </Tooltip>
            </MobileRowValue>
          </MobileRow>

          <MobileRow>
            <MobileRowTitle>{tableColumns[4].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip>{rowSum.distributionsToDate}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>

          <MobileRow>
            <MobileRowTitle>{tableColumns[5].title}</MobileRowTitle>
            <MobileRowValue>
              <FormattingTooltip>{rowSum.marketValue}</FormattingTooltip>
            </MobileRowValue>
          </MobileRow>
        </MobileSumFooterWrap>
      )}
    </>
  );
};

export default CommitmentsMobileSumFooter;

const MobileSumFooterWrap = styled(MobileTableRowWrap)`
  background-color: ${({ theme }) => theme.layer[2]};
`;
